// packages/auth/src/reference-access.ts
import { REFERENCE_PARAMETERS, type ReferenceParameter } from '@prs2/contracts'
import type { Role } from './role.js'
import type { Scope } from './scope.js'

/**
 * Who may READ each pro-ration configuration parameter (Annex A 40–47).
 *
 * ## Why this is not `authorise()`
 *
 * `authorise()` decides four axes about a BILL — agency, bill type, approval
 * level, monetary limit — taken from Annex A story 37, which scopes an approver
 * by exactly those. None of the four is a fact about a pegged ward rate. A
 * reference value has no bill type, no approval level and no amount owed, so
 * reusing that function here would have meant either inventing a synthetic bill
 * to ask about, or widening `authorise()` until it answered two different
 * questions — and an authorisation function that answers two questions is one
 * that will eventually answer the wrong one.
 *
 * The axes that DO apply are two, and both are real:
 *
 *  1. **Role**, per parameter, keyed by this table. Stories 40–47 name their
 *     actor as *"User (PSD User)"* without exception, so PSD administers all
 *     nine; the additional readers below are each named against the thing that
 *     party has to do with the value, never "they are internal".
 *  2. **Agency**, applied **in SQL** by `referenceScopePredicate` in
 *     `@prs2/persistence` — never a `.filter()` after the rows have crossed the
 *     boundary. An HSP reads its own agency's pegged ward rates and the common
 *     entries; another provider's rates never enter this process's memory.
 *
 * ## Deny by default, three times over
 *
 * A `Record<ReferenceParameter, …>` makes the compiler demand an entry for
 * every parameter, so a tenth cannot silently inherit whatever the ninth got.
 * A parameter that bypasses the type entirely reads `undefined` here and is
 * DENIED. And a scope whose role is in no set is denied without a fallback —
 * the failure mode of an authorisation check that defaults open is that nobody
 * notices until the wrong person has seen something.
 *
 * ⚠ The refusal is answered **404, byte-identical to a parameter that does not
 * exist**, by `routes/reference.ts`. A distinguishable 403 would tell an
 * unauthorised caller which configuration parameters this system holds, which
 * is the enumeration oracle every other route here is written to avoid.
 */
const REFERENCE_READ_ROLES: Readonly<Record<ReferenceParameter, ReadonlySet<Role>>> = {
  // The indicator-to-bill-type code table the file-exchange path reads. An HSP
  // needs it to know which token to send; the machine caller needs it to read
  // an incoming file; PSD administers it.
  ADJUSTMENT_INDICATOR: new Set<Role>(['PSD_USER', 'HSP_USER', 'PRS_SERVICE']),
  // Story 41's rates are per-HSP and an HSP is entitled to know the benchmark
  // its own charges are pegged against. The agency axis is what stops it seeing
  // another provider's, and it is applied in SQL.
  PEGGED_WARD_RATE: new Set<Role>(['PSD_USER', 'HSP_USER', 'PRS_SERVICE']),
  // Financial routing (story 42). The parties that act on a settlement or a
  // recovery instruction, and nobody else — an HSP has no business reading the
  // government's internal cost-centre codes.
  ACCOUNT_CODE: new Set<Role>(['PSD_USER', 'PAYMENT_USER', 'RECOVERY_USER', 'PRS_SERVICE']),
  // The MBIM officer issues the records these periods bound (story 43).
  MBIM_MAX_VALIDITY: new Set<Role>(['PSD_USER', 'MBIM_USER', 'PRS_SERVICE']),
  // A service-injury entitlement is an HR determination before it is a bill
  // (story 44), so the Ministry/Agency HR role reads it.
  MIN_WARD_SERVICE_INJURY: new Set<Role>(['PSD_USER', 'MINISTRY_HR_USER', 'PRS_SERVICE']),
  // Story 45 is a deadline an HSP has to meet, and a deadline nobody can read
  // is a deadline nobody can meet. `POST /api/bills/:reference/compute` names
  // this parameter in its refusal, so the caller it refuses can look it up.
  MAX_BILL_GRACE_PERIOD: new Set<Role>(['PSD_USER', 'HSP_USER', 'PRS_SERVICE']),
  // Story 46 bounds what the government subsidises for a child. Not an HSP
  // disclosure: it is a benefit-policy limit, and the HSP is told the split, not
  // the policy behind it.
  CH_CHILD_MAX_SUBSIDISED_LENGTH: new Set<Role>(['PSD_USER', 'MINISTRY_HR_USER', 'PRS_SERVICE']),
  // Story 47's codes are what a community hospital puts on a bill.
  CH_SERVICE_TYPE: new Set<Role>(['PSD_USER', 'HSP_USER', 'PRS_SERVICE']),
  CH_SERVICE_SUBSIDY_ELIGIBILITY: new Set<Role>(['PSD_USER', 'HSP_USER', 'PRS_SERVICE']),
  // Task 8 (Annex A 41, the tenth parameter). An HSP is entitled to know who
  // bears the excess above the benchmark its own charges are pegged against —
  // the same reasoning `PEGGED_WARD_RATE` above already carries — including,
  // and especially, when the answer is "nobody has set one yet".
  PEGGED_RATE_EXCESS_BEARER: new Set<Role>(['PSD_USER', 'HSP_USER', 'PRS_SERVICE']),
}

/** May this scope read this pro-ration configuration parameter at all? */
export function mayReadReferenceParameter(scope: Scope, parameter: ReferenceParameter): boolean {
  // `?? false`, never a permissive fallback: a parameter that bypassed the type
  // — an unvalidated URL segment, a future parameter added to the catalogue but
  // not here — is a question this table does not understand, and denying is the
  // only safe answer to that.
  return REFERENCE_READ_ROLES[parameter]?.has(scope.role) ?? false
}

/** Every parameter this scope may read, in the declaration order of the union. */
export function readableReferenceParameters(scope: Scope): readonly ReferenceParameter[] {
  return REFERENCE_PARAMETERS.filter((parameter) => mayReadReferenceParameter(scope, parameter))
}

/**
 * Roles that may CHANGE pro-ration configuration — supersede a value, or retire
 * an entry.
 *
 * ⚠ **`PSD_USER` and nothing else, including `PRS_SERVICE`.** Every one of
 * stories 40–47 names its actor as *"User (PSD User)"*, and the machine caller
 * is deliberately excluded even though it holds level 2 approval authority on
 * every agency: a change here re-prices bills on a **service-date window**, and
 * the tender gives no batch or automated path any business setting benefit
 * policy. `domain-language.md` §2 already records that a machine caller is not a
 * user with a role and that conflating the two is an authorisation defect.
 *
 * Keyed on role and explicit — the same shape as `mayReadFullAuditTrail` and
 * `mayResolveAnyOfficerEntitlement` — never inferred from how wide a role's bill
 * grants happen to be, because that silently changes who may set policy the next
 * time grants are reshaped for an unrelated reason.
 */
const REFERENCE_ADMIN_ROLES: ReadonlySet<Role> = new Set<Role>(['PSD_USER'])

/** May this scope supersede or retire a pro-ration configuration value? */
export function mayAdministerReferenceData(scope: Scope): boolean {
  return REFERENCE_ADMIN_ROLES.has(scope.role)
}
